import React, {useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import QRCodeSVG from "qrcode.react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import TabChartHeader from "../../components/tabs/TabChartHeader";
import ChartLoadingOverlay from "../../components/Utils/ChartLoadingOverlay";
import {ManagedAssetDoughnut, ManagedAssetLineArea} from "./ManagedAssetStatic";
import {sumCnt} from "../static/AssetDashboardShared";
import {addHeaderTab, openHeaderTabWithChild} from "../../components/pageTabs/pageTabsStorage";

const TYPE_TABS = [
    {label: "자산그룹", key: "group"},
    {label: "업무그룹", key: "task"},
    {label: "자산위치", key: "location"},
    {label: "시스템", key: "project"},
];

const calcPercent = (done, total) => {
    const denominator = Number(total) || 0;
    const numerator = Number(done) || 0;
    if (denominator === 0) return 0;
    return Number(((numerator / denominator) * 100).toFixed(2));
};

const formatBoardDate = (createdAt) => {
    if (!createdAt || typeof createdAt !== "string") return "";
    return createdAt.length >= 16 ? createdAt.substring(5, 16) : createdAt;
};

function ChartTooltip({active, payload, label}) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-[4px] border border-[rgba(0,0,0,0.08)] bg-white px-3 py-2 text-[12px] shadow-sm">
            <div className="font-medium text-[#333]">{label}</div>
            <div className="text-[#666]">건수 : {Number(payload[0]?.value || 0).toLocaleString()}</div>
        </div>
    );
}

export function AssetTypeTabCard({data, loading = false}) {
    const [tabAttribute, setTabAttribute] = useState(
        TYPE_TABS.map((tab, index) => ({clicked: index === 0, name: tab.label}))
    );

    const activeTab = tabAttribute.find((tab) => tab.clicked);
    const activeKey = TYPE_TABS.find((tab) => tab.label === activeTab?.name)?.key ?? "group";
    const tabData = data?.data ?? data ?? {};
    const items = tabData[activeKey] ?? tabData.locationb ?? [];
    const total = useMemo(() => sumCnt(items), [items]);

    return (
        <div className="relative flex h-full min-h-0 w-full flex-col border rounded-si p-[16px]">
            {loading && <ChartLoadingOverlay />}
            <div className="shrink-0">
                <TabChartHeader
                    title="자산유형별 현황"
                    variant="split"
                    args={tabAttribute}
                    setArgs={setTabAttribute}
                />
            </div>
            <div className="flex min-h-0 flex-1">
                <div className="mobile-hidden h-full w-[44%] min-w-0 shrink-0 overflow-hidden">
                    <ManagedAssetDoughnut items={items} total={total} />
                </div>
                <div className="mobile-full h-full min-w-0 flex-1 overflow-hidden">
                    <ManagedAssetLineArea items={items} />
                </div>
            </div>
        </div>
    );
}

export function AssetLifeCycleCard({data, loading = false, title = "라이프 사이클"}) {
    const points = useMemo(() => {
        const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        return list.map((item) => ({
            name: String(item.name ?? ""),
            value: Number(item.cnt) || 0,
        }));
    }, [data]);

    return (
        <div className="relative flex h-full min-h-0 w-full flex-col border rounded-si p-[16px]">
            {loading && <ChartLoadingOverlay />}
            <h3 className="mb-2 shrink-0 text-[21px] font-bold leading-none text-[#333]">
                {data?.title ?? title}
            </h3>
            <div className="relative min-h-0 flex-1">
                <div className="absolute inset-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={points} margin={{top: 4, right: 4, left: -20, bottom: 0}}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                            <XAxis
                                dataKey="name"
                                tick={{fontSize: 10, fill: "#666"}}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis tick={{fontSize: 11, fill: "#666"}} axisLine={false} tickLine={false} />
                            <Tooltip content={<ChartTooltip />} cursor={false} />
                            <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={28} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

export function AssetDiligenceCard({data, loading = false, period}) {
    const navigate = useNavigate();
    const FE_URL = import.meta.env.VITE_FE_URL;

    const payload = data?.data?.left != null || data?.data?.right != null ? data.data : data?.data ?? data;
    const left = payload?.left ?? [];
    const right = payload?.right ?? [];
    const diligenceId = right[2]?.cnt ?? right[2]?.id;

    const recentTitle =
        data?.recentName ??
        data?.title ??
        payload?.recentName ??
        payload?.title ??
        (diligenceId != null ? "진행 중인 실사" : "진행 중인 실사 없음");

    const recentDone = Number(left[1]?.cnt) || 0;
    const recentTotal = Number(left[0]?.cnt) || 0;
    const recentPercent = calcPercent(recentDone, recentTotal);
    const cumulativePercent = calcPercent(right[1]?.cnt, right[0]?.cnt);

    const goToDiligence = () => navigate("/diligence");
    const goToReport = () => diligenceId != null && navigate(`/diligence/${diligenceId}/report`);
    const goToInfo = () => diligenceId != null && navigate(`/diligence/${diligenceId}`);

    return (
        <div className="relative flex h-full min-h-0 w-full flex-col">
            {loading && <ChartLoadingOverlay />}

            <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
                <h3 className="text-[21px] font-bold leading-none text-[#333]">자산실사</h3>
                {period && (
                    <span className="text-[12px] leading-none text-[#94a3b8]">{period}</span>
                )}
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-2">
                <div className="grid min-h-0 flex-1 grid-cols-2 gap-2">
                    <div className="flex h-full min-h-[48px] flex-col items-center justify-center rounded-[8px] bg-[#eff6ff] px-2">
                        <span className="text-[24px] font-bold leading-none text-[#3b82f6]">
                            {recentPercent}%
                        </span>
                        <span className="mt-1 text-[11px] font-medium text-[#64748b]">최근 실사율</span>
                    </div>
                    <div className="flex h-full min-h-[48px] flex-col items-center justify-center rounded-[8px] bg-[#f8fafc] px-2">
                        <span className="text-[24px] font-bold leading-none text-[#3b82f6]">
                            {cumulativePercent}%
                        </span>
                        <span className="mt-1 text-[11px] font-medium text-[#64748b]">누적 실사율</span>
                    </div>
                </div>

                <div className="flex min-h-0 flex-1 flex-col justify-center gap-2 rounded-[8px] border border-[#e5e7eb] px-3">
                    <div className="min-w-0">
                        <spanp className="text-[11px] font-medium text-[#64748b] bg-white inline-block px-1">최근실사</spanp>
                        <p
                            className="mt-1.5 truncate text-[12px] font-semibold text-[#334155]"
                            title={recentTitle}
                        >
                            {recentTitle}
                        </p>
                    </div>
                    <div className="h-[6px] shrink-0 overflow-hidden rounded-full bg-[#e5e7eb]">
                        <div
                            className="h-full rounded-full bg-[#3b82f6] transition-[width] duration-300"
                            style={{width: `${Math.min(100, recentPercent)}%`}}
                        />
                    </div>
                </div>

                <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto_auto] items-stretch gap-2">
                    <button
                        type="button"
                        onClick={goToInfo}
                        disabled={diligenceId == null}
                        className="flex min-h-[36px] min-w-0 items-center gap-2 rounded-[8px] border border-[#e5e7eb] bg-white px-2 text-left transition-colors hover:bg-[#f8fafc] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {diligenceId != null ? (
                            <QRCodeSVG
                                value={`${FE_URL}diligence/${diligenceId}/handle`}
                                size={28}
                            />
                        ) : (
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-[#f1f5f9] text-[10px] text-[#94a3b8]">
                                QR
                            </span>
                        )}
                        <span className="truncate text-[12px] font-semibold text-[#334155]">실사정보</span>
                    </button>
                    <button
                        type="button"
                        onClick={goToDiligence}
                        className="min-h-[36px] rounded-[8px] border border-[#e5e7eb] bg-white px-3 text-[12px] font-semibold text-[#334155] transition-colors hover:bg-[#f8fafc]"
                    >
                        대장
                    </button>
                    <button
                        type="button"
                        onClick={goToReport}
                        disabled={diligenceId == null}
                        className="min-h-[36px] rounded-[8px] bg-[#22c55e] px-3 text-[12px] font-semibold text-white transition-colors hover:bg-[#16a34a] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        보고서
                    </button>
                </div>
            </div>
        </div>
    );
}

export function AssetBoardListCard({title, items, loading = false, listPath, listTitle}) {
    const navigate = useNavigate();
    const safeItems = Array.isArray(items) ? items : [];

    const handleRowClick = (item) => {
        if (!item?.link || item?.id == null) return;
        const detailUrl = `${item.link}/${item.id}`;
        if (listPath && listTitle) {
            openHeaderTabWithChild({
                header: {title: listTitle, url: listPath, icon: "setting"},
                child: {title: item.subject || listTitle, url: detailUrl},
            });
        }
        navigate(detailUrl);
    };

    return (
        <div className="relative flex h-full min-h-0 w-full flex-col border rounded-si p-[16px]">
            {loading && <ChartLoadingOverlay />}
            <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
                <h3 className="text-[21px] font-bold leading-none text-[#333]">{title}</h3>
                {listPath && (
                    <button
                        type="button"
                        onClick={() => {
                            if (listTitle) {
                                addHeaderTab({title: listTitle, url: listPath, icon: "setting"});
                            }
                            navigate(listPath);
                        }}
                        className="shrink-0 text-[13px] text-[#9ca3af] transition-colors hover:text-[#6b7280]"
                    >
                        더보기 &gt;
                    </button>
                )}
            </div>

            {safeItems.length > 0 ? (
                <ul className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                    {safeItems.map((item, index) => (
                        <li key={item.id ?? index}>
                            <button
                                type="button"
                                onClick={() => handleRowClick(item)}
                                className="flex w-full items-center justify-between gap-3 border-b border-[#eeeeee] py-2.5 text-left last:border-b-0 hover:bg-[#fafafa]"
                            >
                                <span className="min-w-0 flex-1 truncate text-[13px] text-[#111827]">
                                    {item.subject ?? ""}
                                </span>
                                <span className="shrink-0 text-[12px] text-[#9ca3af]">
                                    [{formatBoardDate(item.createdAt)}]
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="flex flex-1 items-center justify-center text-[13px] text-[#9ca3af]">
                    No Data.
                </div>
            )}
        </div>
    );
}
